import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideFileUp, lucidePlus } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { CompanyService } from '../../../../../services/company.service';
import {
  VoucherDesign,
  VoucherDesignService,
  VoucherType,
} from '../../../../../services/voucher-design.service';
import { DesignCardComponent } from './design-card/design-card.component';

type Filter = 'all' | VoucherType;

/** Every design the company has, filterable by voucher type. */
@Component({
  selector: 'app-design-library',
  standalone: true,
  imports: [
    NgIcon,
    HlmButtonImports,
    HlmCardImports,
    HlmEmptyImports,
    HlmSkeletonImports,
    DesignCardComponent,
  ],
  providers: [provideIcons({ lucideFileUp, lucidePlus })],
  templateUrl: './design-library.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesignLibraryComponent {
  private readonly designService = inject(VoucherDesignService);
  private readonly companyService = inject(CompanyService);

  readonly designs = input<VoucherDesign[]>([]);
  readonly loading = input(false);
  readonly busyId = input<string | null>(null);

  readonly activate = output<VoucherDesign>();
  readonly copy = output<{ design: VoucherDesign; voucherType: VoucherType }>();
  readonly delete = output<VoucherDesign>();
  readonly create = output<void>();

  readonly filter = signal<Filter>('all');
  readonly filters: { value: Filter; label: string }[] = [
    { value: 'all', label: 'Alle' },
    { value: 'money', label: 'Wert' },
    { value: 'product', label: 'Produkt' },
  ];
  /** Filters only pay off with designs of both types and more than a handful of them. */
  readonly showFilters = computed(() => {
    const designs = this.designs();
    const types = new Set(designs.map((d) => d.voucher_type));
    return types.size > 1 && designs.length > 4;
  });
  readonly shown = computed(() => {
    const filter = this.showFilters() ? this.filter() : 'all';
    return this.designs().filter((d) => filter === 'all' || d.voucher_type === filter);
  });

  isActive(design: VoucherDesign): boolean {
    return this.designService.activeDesign(this.designs(), design.voucher_type) === design;
  }
}
